import { useZhodaCutRite } from "@/api/hooks/usePorez";
import { formatPct } from "@/lib/format";

const pct1 = (v: string | number | null | undefined): string => formatPct(v, 1);

/**
 * Zhoda s Cut Rite (KOLO 213) — pre posledné plány, ktoré pomocník poslal
 * späť, porez Rozpočtu prepočítaný zo súboru, ktorý išiel do Cut Rite.
 * User: *„aby tie nárezové plány sa zhodovali s nárezovými plánmi z Cut
 * Rite"* — tu vidno, či a kde nie. Rozdiely hore.
 */
export function ZhodaCutRiteSection(): JSX.Element {
  const { data, isLoading } = useZhodaCutRite();
  const zhoda = data?.data;
  const riadky = (zhoda?.odoslania ?? []).flatMap((o) =>
    o.materialy.map((m) => ({ o, m })),
  );
  // rozdiely navrch, potom zhody, neporovnané na koniec
  const poradie = (z: boolean | null | undefined): number =>
    z === false ? 0 : z === true ? 1 : 2;
  riadky.sort((a, b) => poradie(a.m.zhoda) - poradie(b.m.zhoda));

  return (
    <section
      className="space-y-3 rounded-lg border bg-card p-4"
      aria-labelledby="zhoda-cutrite-nadpis"
    >
      <div>
        <h2 id="zhoda-cutrite-nadpis" className="text-xl">
          Zhoda s Cut Rite
        </h2>
        <p className="text-xs text-muted-foreground">
          Porez Rozpočtu prepočítaný zo súborov, ktoré išli do Cut Rite, vedľa
          plánov, ktoré Cut Rite naozaj spravil (posledných 30). Do ceny ide
          plán z Cut Rite, kým sa rezanie nezmení — toto ukazuje, ako presne
          odhaduje Rozpočet, kým plán ešte nie je.
        </p>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Načítavam…</p>
      ) : !zhoda || zhoda.odoslania.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Zatiaľ žiadny plán z Cut Rite. Pomocník 213 ho pošle po každej
          optimalizácii (Nastavenia → Prepojenia → Pomocník pre Cut Rite).
        </p>
      ) : (
        <>
          <p className="text-sm">
            Rovnaký počet tabúľ pri{" "}
            <strong>
              {zhoda.rovnako} z {zhoda.porovnane}
            </strong>{" "}
            materiálov.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                  <th className="pb-1 pr-2 font-medium">Zákazka</th>
                  <th className="pb-1 pr-2 font-medium">Materiál</th>
                  <th className="pb-1 pr-2 text-right font-medium">Cut Rite</th>
                  <th className="pb-1 text-right font-medium">Rozpočet</th>
                </tr>
              </thead>
              <tbody>
                {riadky.map(({ o, m }) => (
                  <tr key={`${o.odoslanie_id}-${m.kod}`} className="border-t">
                    <td className="py-1 pr-2" title={o.nazov_suboru}>
                      {o.zakazka}
                    </td>
                    <td className="py-1 pr-2 font-mono">{m.kod}</td>
                    <td className="whitespace-nowrap py-1 pr-2 text-right font-mono">
                      {m.cutrite_tabule} · {pct1(m.cutrite_pct)}
                    </td>
                    <td
                      className={`py-1 text-right font-mono ${
                        m.rozpocet_tabule == null ? "" : "whitespace-nowrap"
                      }`}
                    >
                      {m.rozpocet_tabule == null ? (
                        // dôvod viditeľne — na mobile tooltip nie je
                        <span className="text-muted-foreground">
                          <span className="whitespace-nowrap">neporovnané</span>
                          {m.poznamka ? (
                            <span className="block font-sans text-[10px]">{m.poznamka}</span>
                          ) : null}
                        </span>
                      ) : (
                        <>
                          {m.rozpocet_tabule} · {pct1(m.rozpocet_pct)}
                          {m.zhoda ? (
                            <span
                              className="ml-1 text-emerald-700 dark:text-emerald-400"
                              aria-label="rovnako"
                            >
                              ✓
                            </span>
                          ) : (
                            <span
                              className="ml-1 text-amber-700 dark:text-amber-400"
                              aria-label="inak"
                            >
                              ≠
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
